import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { api, BracketStage, Feeders, Match, TimbaPrediction } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { TimbaTournaments } from '../components/TimbaTournaments';
import { Flag } from '../components/TeamBadge';
import { teamName } from '../lib/flags';
import { Muted } from '../components/ui';
import { getIdentity } from '../lib/identity';
import { buildGraph, bracketHalf, realAdvancer } from '../lib/simulate';
import { formatPick, potentialMultipliers } from '../lib/timba';
import { timbaBracketSlots } from '../lib/timbaBracket';
import { useStore } from '../stores/store';
import { TimbaHelp } from '../components/TimbaHelp';

const LOCK_MS = 0; // los pronósticos se cierran al kickoff (arranque del partido)
const SPLIT_ROUNDS = ['r32', 'r16', 'qf', 'sf'];
const CENTER_ROUNDS = ['third', 'final'];

type PredMap = Record<number, TimbaPrediction>;

const isFinished = (m: Match) => m.status === 'finished' && m.score != null;

// Orden cronológico ascendente por horario de arranque (el más próximo arriba). Los partidos sin
// horario van al final. Se usa dentro de cada columna de la llave (mitad A / mitad B).
const byKickoff = (a: Match, b: Match) => {
  const ta = a.kickoffUtc ? new Date(a.kickoffUtc).getTime() : Infinity;
  const tb = b.kickoffUtc ? new Date(b.kickoffUtc).getTime() : Infinity;
  return ta - tb;
};

// Sección de ronda colapsable. Default: con partidos activos arranca abierta; si la fase ya terminó,
// colapsada. Si el usuario la abre/cierra a mano, esa elección PERSISTE (store `timbaRoundCollapse`,
// override por fase) y gana sobre el default.
function RoundSection({ id, label, done, children }: { id: string; label: string; done: boolean; children: ReactNode }) {
  const override = useStore((s) => s.timbaRoundCollapse[id]); // true=colapsada · false=abierta · undefined=default
  const setCollapse = useStore((s) => s.setTimbaRoundCollapse);
  const open = override === undefined ? !done : !override;
  return (
    <div>
      <RoundHead type="button" className={done ? 'done' : ''} aria-expanded={open} onClick={() => setCollapse(id, open)}>
        <span className="lbl">{label}</span>
        {done && <span className="tag">Finalizada</span>}
        <span className="chev">{open ? '▾' : '▸'}</span>
      </RoundHead>
      {open && children}
    </div>
  );
}

function TimbaBracket({ stages, feeders }: { stages: BracketStage[]; feeders: Feeders }) {
  const pub = useMemo(() => {
    try {
      return getIdentity().pub;
    } catch {
      return '';
    }
  }, []);
  const [preds, setPreds] = useState<PredMap>({});
  const predsMap = useMemo(() => new Map(Object.entries(preds).map(([k, v]) => [Number(k), v])), [preds]);
  // Resultados reales (lado que avanzó) de los cruces ya definidos: para saber qué realce sigue vivo.
  const results = useMemo(() => {
    const r = new Map<number, 1 | 2>();
    for (const m of stages.flatMap((s) => s.matches)) {
      const adv = m.num != null ? realAdvancer(m.score) : null;
      if (adv !== null) r.set(m.num as number, adv);
    }
    return r;
  }, [stages]);
  // `optim` = lo máximo que daría si TODO te sale (ignora resultados); `reachable` = lo que TODAVÍA
  // se puede dar mirando los partidos ya jugados. Si reachable < optim → algo ya se cayó.
  const optim = useMemo(() => potentialMultipliers(predsMap, feeders), [predsMap, feeders]);
  const reachable = useMemo(() => potentialMultipliers(predsMap, feeders, results), [predsMap, feeders, results]);

  useEffect(() => {
    if (!pub) return;
    let alive = true;
    api
      .timbaPredictions(pub)
      .then((d) => {
        if (!alive) return;
        const map: PredMap = {};
        d.predictions.forEach((p) => (map[p.num] = p));
        setPreds(map);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [pub]);

  const g = useMemo(() => buildGraph(stages, feeders), [stages, feeders]);
  const slots = useMemo(() => timbaBracketSlots(stages, preds), [stages, preds]);
  const stageBy = (key: string) => stages.find((s) => s.key === key);

  // Atajo a la tarea primaria (auditoría #8): el primer cruce ABIERTO sin pronóstico, por orden
  // de kickoff. Abre la ronda si estaba colapsada y scrollea hasta la card.
  const setCollapse = useStore((s) => s.setTimbaRoundCollapse);
  const nextPending = useMemo(() => {
    const candidates = stages.flatMap((s) =>
      s.matches
        .filter((m) => m.num != null && !preds[m.num as number])
        .filter((m) => !!m.kickoffUtc && Date.now() < new Date(m.kickoffUtc).getTime() && !(m.status === 'finished' && m.score != null))
        .map((m) => ({ match: m, stageKey: s.key })),
    );
    candidates.sort((a, b) => byKickoff(a.match, b.match));
    return candidates[0] ?? null;
  }, [stages, preds]);
  const pendingLabel = (() => {
    if (!nextPending) return '';
    const [a, b] = slots.get(nextPending.match.num as number) ?? [];
    const nm = (s?: { team: string | null; label: string; confirmed: boolean }) => (s?.team ? teamName(s.team) : s?.label || '—');
    return `${nm(a)} vs ${nm(b)}`;
  })();
  const goToNextPending = () => {
    if (!nextPending) return;
    setCollapse(nextPending.stageKey, false); // asegura la ronda abierta antes de scrollear
    setTimeout(() => {
      document.getElementById(`timba-match-${nextPending.match.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  };

  const cell = (m: Match) => {
    const [s1, s2] = slots.get(m.num as number) ?? [
      { team: null, label: '', confirmed: false },
      { team: null, label: '', confirmed: false },
    ];
    const p = preds[m.num as number];
    const finished = m.status === 'finished' && m.score != null;
    const live = m.status === 'live'; // partido en curso → badge VIVO titilando
    const pen = m.score?.pen ?? null;                  // tanda: [pa, pb] o null
    const adv = finished ? realAdvancer(m.score) : null; // 1|2 = quién avanzó (90'→alargue→penales)
    const open = !!m.kickoffUtc && Date.now() < new Date(m.kickoffUtc).getTime() - LOCK_MS && !finished;
    const hit = finished && p?.points != null && p.points > 0; // "embocado": tu pronóstico sumó
    const miss = finished && !hit; // terminó y no sumó (pick errado o sin pronóstico): franja + contorno gris
    // Rivales tentativos: hay equipos pero alguno todavía no está confirmado (proyectado o por pick).
    const teamsKnown = s1.team !== null && s2.team !== null;
    const tentative = (!!s1.team && !s1.confirmed) || (!!s2.team && !s2.confirmed);
    const status = finished
      ? 'Finalizado'
      : open
        ? tentative ? 'Tentativo' : 'Abierto'
        : 'Cerrado';

    // Booster/bonus en tres estados: CONFIRMADO (backend, partido jugado), POTENCIAL (todavía
    // alcanzable) o NO ALCANZABLE (un alimentador ya jugó en contra y lo mató). Solo se muestran si
    // el cruce ya tiene los DOS equipos definidos (reales o tentativos).
    const confirmed = finished && p?.points != null;
    const reach = finished ? undefined : reachable.get(m.num as number);
    const opt = finished ? undefined : optim.get(m.num as number);
    const alive = !!reach && (reach.booster > 1 || reach.bonus > 0); // potencial vivo
    const lost = !alive && !!opt && (opt.booster > 1 || opt.bonus > 0); // tenía realce y se cayó del todo
    const variant: 'confirmed' | 'potential' | 'lost' = confirmed ? 'confirmed' : alive ? 'potential' : 'lost';
    const booster = confirmed ? p!.booster ?? 1 : alive ? reach!.booster : lost ? opt!.booster : 1;
    const bonus = confirmed ? p!.bonus ?? 0 : alive ? reach!.bonus : lost ? opt!.bonus : 0;
    const showBadges = teamsKnown && (confirmed || alive || lost) && (booster > 1 || bonus > 0);

    return (
      <Cell key={m.id} id={`timba-match-${m.id}`} to={`/match/${m.id}`} $open={open} $picked={!!p} $hit={hit} $miss={miss}>
        <Row className={s1.team && !s1.confirmed ? 'tent' : ''}>
          <Flag team={s1.team} size={16} />
          <span className={s1.team ? 'nm' : 'nm ph'}>{s1.team ? teamName(s1.team) : s1.label}</span>
          {(finished || live) && m.score && <span className={`go ${adv === 1 ? 'win' : ''}${live ? ' prov' : ''}`}>{m.score.ft[0]}{pen && <em>({pen[0]})</em>}</span>}
        </Row>
        <Row className={s2.team && !s2.confirmed ? 'tent' : ''}>
          <Flag team={s2.team} size={16} />
          <span className={s2.team ? 'nm' : 'nm ph'}>{s2.team ? teamName(s2.team) : s2.label}</span>
          {(finished || live) && m.score && <span className={`go ${adv === 2 ? 'win' : ''}${live ? ' prov' : ''}`}>{m.score.ft[1]}{pen && <em>({pen[1]})</em>}</span>}
        </Row>
        {showBadges && (
          <Badges
            $variant={variant}
            title={
              (variant === 'confirmed'
                ? 'Realce confirmado'
                : variant === 'potential'
                  ? 'Realce potencial (punteado: todavía alcanzable)'
                  : 'Realce ya no alcanzable (tachado)') +
              ' — booster ×N y bonus de llave 🔑. Detalle en «Cómo se juega».'
            }
          >
            {booster > 1 && <span className="boost">×{booster}</span>}
            {bonus > 0 && <span className="llave">🔑 +{bonus}</span>}
          </Badges>
        )}
        <Foot $picked={!!p} $hit={hit} $miss={miss}>
          <span className="pick">{p ? `Tu: ${formatPick(p, s1.team, s2.team)}` : finished ? 'Sin pronóstico' : open ? 'Pronosticá' : '—'}</span>
          {finished ? (
            p?.points != null ? (
              <span className={`pts ${p.points > 0 ? 'hit' : ''}`}>+{p.points}</span>
            ) : (
              <span className="st">—</span>
            )
          ) : live ? (
            <span className="live"><i className="led" />VIVO</span>
          ) : (
            <span className={`st ${open ? 'on' : ''}`}>{status}</span>
          )}
        </Foot>
      </Cell>
    );
  };

  return (
    <>
      {nextPending && (
        <NextChip type="button" onClick={goToNextPending}>
          ⚡ Próximo sin pronóstico:
          <span className="who">{pendingLabel}</span>
          <span className="go">Ir ↓</span>
        </NextChip>
      )}
      {SPLIT_ROUNDS.map((key) => {
        const st = stageBy(key);
        if (!st) return null;
        const left = st.matches.filter((m) => bracketHalf(m.num as number, g) === 'A').sort(byKickoff);
        const right = st.matches.filter((m) => bracketHalf(m.num as number, g) === 'B').sort(byKickoff);
        const done = st.matches.length > 0 && st.matches.every(isFinished);
        return (
          <RoundSection key={key} id={key} label={st.label} done={done}>
            <TwoCol>
              <div>{left.map(cell)}</div>
              <div>{right.map(cell)}</div>
            </TwoCol>
          </RoundSection>
        );
      })}
      {CENTER_ROUNDS.map((key) => {
        const st = stageBy(key);
        if (!st) return null;
        const done = st.matches.length > 0 && st.matches.every(isFinished);
        return (
          <RoundSection key={key} id={key} label={st.label} done={done}>
            {st.matches.map(cell)}
          </RoundSection>
        );
      })}
    </>
  );
}

export function Timba() {
  const state = useAsync(() => api.bracket(), [], '/bracket');
  const [helpOpen, setHelpOpen] = useState(false);
  const startRoute = useStore((s) => s.startRoute);
  const setStartRoute = useStore((s) => s.setStartRoute);
  const homeStart = startRoute === '/'; // el usuario optó por arrancar en "Hoy" (la Timba es el default)

  return (
    <>
      <TopBar>
        {/* Toggle con label de ACCIÓN constante (auditoría #9: antes el texto describía el
            estado y tocarlo hacía lo contrario). Lleno = activo; aria-pressed para el lector. */}
        <button type="button" className={!homeStart ? 'pin on' : 'pin'} aria-pressed={!homeStart} onClick={() => setStartRoute(homeStart ? '/timba' : '/')}>
          📌 Arrancar en la Timba
        </button>
        <button type="button" className="help" onClick={() => setHelpOpen(true)}>ℹ️ Cómo se juega</button>
      </TopBar>

      <Intro>
        <h2>🎲 Timba</h2>
        <Muted style={{ lineHeight: 1.55 }}>
          Pronosticá cada partido de la fase de llave: tocá un cruce, cargá el marcador y (si es empate) quién
          gana en el alargue o los penales. Sumás puntos por acertar el resultado y el marcador. Armá torneos
          privados con tus amigos o competí en el ranking global.
        </Muted>
      </Intro>

      <TimbaTournaments />

      {/* Leyenda de realces (auditoría #10): el significado vivía solo en tooltips de hover
          (inaccesibles en touch) y en el modal de ayuda. Una línea, con acceso a la ayuda. */}
      <Legend>
        <span className="tag solid">×2</span> confirmado
        <span className="tag dash">×2</span> alcanzable
        <span className="tag lost">×2</span> perdido
        <span className="tag key">🔑+1</span> por equipo que llega
        <button type="button" onClick={() => setHelpOpen(true)}>ℹ️ más</button>
      </Legend>

      <Async state={state}>{(d) => <TimbaBracket stages={d.stages} feeders={d.feeders} />}</Async>

      {helpOpen && <TimbaHelp onClose={() => setHelpOpen(false)} />}
    </>
  );
}

const Intro = styled.div`
  margin-bottom: 14px;
  h2 { margin: 0 0 6px; font-size: var(--text-xl); }
`;

const TopBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  button {
    flex: 1;
    padding: 9px 10px;
    border-radius: 999px;
    font-size: var(--text-sm);
    font-weight: 700;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: none;
    color: ${({ theme }) => theme.colors.text};
  }
  .pin.on { background: ${({ theme }) => theme.colors.accent}; color: ${({ theme }) => theme.colors.accentText}; border-color: ${({ theme }) => theme.colors.accent}; }
`;

const NextChip = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 2px 0 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1.5px dashed ${({ theme }) => theme.colors.accentInk};
  background: none;
  color: ${({ theme }) => theme.colors.accentInk};
  font-weight: 800;
  font-size: var(--text-sm);
  text-align: left;
  white-space: nowrap;
  .who { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; font-weight: 700; color: ${({ theme }) => theme.colors.text}; }
  .go { flex: 0 0 auto; font-size: var(--text-xs); }
`;

// Leyenda compacta de los realces. El tag "perdido" usa textMuted (no `miss`): como texto
// chico, `miss` no llega a 4.5:1 en tema claro; el gris apagado + tachado conservan la señal.
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px 10px;
  margin: 0 0 12px;
  font-size: var(--text-2xs);
  color: ${({ theme }) => theme.colors.textMuted};
  .tag { font-weight: 900; font-size: var(--text-2xs); padding: 1px 6px; border-radius: 6px; border: 1.5px solid; }
  .tag.solid { color: ${({ theme }) => theme.colors.boost}; border-color: ${({ theme }) => theme.colors.boost}; }
  .tag.dash { color: ${({ theme }) => theme.colors.boost}; border-color: ${({ theme }) => theme.colors.boost}; border-style: dashed; }
  .tag.lost { color: ${({ theme }) => theme.colors.textMuted}; border-color: ${({ theme }) => theme.colors.miss}; text-decoration: line-through; }
  .tag.key { color: ${({ theme }) => theme.colors.gold}; border-color: ${({ theme }) => theme.colors.gold}; }
  button { background: none; border: none; padding: 0; min-height: 0; color: ${({ theme }) => theme.colors.accentInk}; font-weight: 700; font-size: var(--text-xs); }
`;

const TwoCol = styled.div`
  display: grid;
  /* minmax(0, 1fr) (no 1fr): permite que las columnas encojan por debajo del contenido, así los
     nombres largos truncan con «…» y la grilla no se pasa del ancho del viewport (antes se recortaba). */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  align-items: start;
`;

const RoundHead = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  .lbl { flex: 1; text-align: left; font-size: var(--text-base); font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
  .tag {
    flex: 0 0 auto; font-size: var(--text-2xs); font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textMuted};
    border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 999px; padding: 2px 8px;
  }
  .chev { flex: 0 0 auto; font-size: var(--text-sm); color: ${({ theme }) => theme.colors.textMuted}; }
  &.done .lbl { color: ${({ theme }) => theme.colors.textMuted}; }
`;

// $picked: tengo pronóstico → card celeste (con franja inferior celeste). $hit: emboqué (sumó
// puntos) → mismo fondo celeste pero el FILETE (borde) en dorado + glow, para que salte la ganadora.
// $miss: terminó sin sumar → misma franja/contorno, pero en gris (partido jugado y perdido).
const Cell = styled(Link)<{ $open: boolean; $picked: boolean; $hit: boolean; $miss: boolean }>`
  display: block;
  overflow: hidden;
  background: ${({ theme, $picked, $miss }) =>
    $miss ? 'rgba(148, 163, 184, 0.10)' : $picked ? 'rgba(56, 189, 248, 0.13)' : theme.colors.surface};
  border: 1.5px solid ${({ theme, $open, $picked, $hit, $miss }) =>
    $hit ? theme.colors.hit : $miss ? theme.colors.miss : $picked ? theme.colors.pick : $open ? theme.colors.accentBorder : theme.colors.border};
  border-left: ${({ theme, $picked, $hit, $miss }) =>
    $hit ? `5px solid ${theme.colors.hit}` : $miss ? `4px solid ${theme.colors.miss}` : $picked ? `4px solid ${theme.colors.pick}` : '1px solid transparent'};
  border-radius: 10px;
  padding: 9px 10px;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.text};
  ${({ $hit, theme }) => $hit && `box-shadow: 0 0 0 1px ${theme.colors.hit}, 0 4px 16px ${theme.colors.hit}55;`}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  .nm { flex: 1; font-weight: 600; font-size: var(--text-sm); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .nm.ph { color: ${({ theme }) => theme.colors.textMuted}; font-style: italic; font-weight: 500; font-size: var(--text-xs); }
  .go { flex: 0 0 auto; min-width: 20px; text-align: right; font-size: var(--text-xl); font-weight: 900; line-height: 1; font-variant-numeric: tabular-nums; color: ${({ theme }) => theme.colors.textMuted}; }
  .go.win { color: ${({ theme }) => theme.colors.text}; }
  /* Marcador PARCIAL de un partido en vivo: rojo "en juego" (unificado con MatchCard) + titilado lento
     para que se lea como provisional/sin confirmar, distinto del score final (color sólido). */
  .go.prov { color: ${({ theme }) => theme.colors.live}; animation: liveScore 2.2s ease-in-out infinite; }
  @keyframes liveScore { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
  .go em { font-size: 0.6em; font-style: normal; font-weight: 800; margin-left: 1px; color: ${({ theme }) => theme.colors.accentInk}; }
  & + & { margin-top: 3px; }
  /* Rival tentativo (proyectado del grupo): en gris hasta que se confirme. */
  &.tent { filter: grayscale(1); opacity: 0.5; }
`;

// Badges de booster (×N, ámbar) y bonus de llave (🔑, azul). Sólido = confirmado; punteado =
// potencial (todavía alcanzable); gris + tachado = no alcanzable (un alimentador ya jugó en contra).
const Badges = styled.div<{ $variant: 'confirmed' | 'potential' | 'lost' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  opacity: ${({ $variant }) => ($variant === 'confirmed' ? 1 : $variant === 'potential' ? 0.8 : 0.65)};
  span {
    font-size: var(--text-md);
    font-weight: 900;
    padding: 4px 10px;
    border-radius: 8px;
    border: ${({ $variant }) => ($variant === 'confirmed' ? '1.5px solid' : '1.5px dashed')};
  }
  ${({ $variant, theme }) =>
    $variant === 'lost'
      ? `.boost, .llave { color: ${theme.colors.miss}; border-color: ${theme.colors.miss}; text-decoration: line-through; }`
      : `.boost { color: ${theme.colors.boost}; border-color: ${theme.colors.boost}; }
         .llave { color: ${theme.colors.gold}; border-color: ${theme.colors.gold}; }`}
  .tag { color: ${({ theme }) => theme.colors.textMuted}; border-color: transparent; font-weight: 700; font-size: var(--text-2xs); text-transform: uppercase; letter-spacing: 0.5px; padding-left: 0; }
`;

// Franja inferior: SIEMPRE una banda con la misma geometría (sangra a los bordes, mismo margen/padding
// y 1px de borde superior) para que TODAS las cards midan igual el pie. Solo cambia el color de fondo:
// CELESTE (embocado) · GRIS (terminó sin sumar) · TRANSPARENTE + línea separadora (el resto). El
// puntaje embocado va en una píldora DORADA (bien evidente).
const Foot = styled.div<{ $picked: boolean; $hit: boolean; $miss: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin: 8px -10px -9px;
  padding: 8px 10px;
  border-top: 1px solid ${({ $hit, $miss, theme }) => ($hit || $miss ? 'transparent' : theme.colors.border)};
  background: ${({ $hit, $miss, theme }) => ($hit ? theme.colors.pick : $miss ? theme.colors.miss : 'transparent')};
  .pick {
    flex: 1; min-width: 0; /* ocupa lo disponible y trunca con «…»: el pie NUNCA pasa de una línea */
    font-size: var(--text-xs); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    color: ${({ $hit, $miss, $picked, theme }) => ($hit ? theme.colors.pickText : $miss ? '#1f2937' : $picked ? theme.colors.pick : theme.colors.textMuted)};
    font-weight: ${({ $picked }) => ($picked ? 800 : 400)};
  }
  .st {
    flex: 0 0 auto; white-space: nowrap; /* estado a la derecha, fijo y sin envolver */
    font-size: var(--text-2xs); font-weight: 700; text-transform: uppercase;
    color: ${({ $hit, $miss, theme }) => ($hit ? theme.colors.pickText : $miss ? '#1f2937' : theme.colors.textMuted)};
  }
  .st.on { color: ${({ $hit, $miss, theme }) => ($hit ? theme.colors.pickText : $miss ? '#1f2937' : theme.colors.accentInk)}; }
  /* Partido en curso: LED rojo + "VIVO" (mismo rojo "en juego" que MatchCard), ambos titilando. */
  .live {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: var(--text-2xs);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.live};
    animation: blink 1.1s ease-in-out infinite;
  }
  .live .led {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.live};
    box-shadow: 0 0 6px ${({ theme }) => theme.colors.live};
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
  }
  .pts {
    flex: 0 0 auto; font-weight: 900; font-size: var(--text-md);
    padding: 2px 9px; border-radius: 999px;
    background: rgba(3, 30, 44, 0.16); color: ${({ theme }) => theme.colors.pickText};
  }
  .pts.hit {
    background: ${({ theme }) => theme.colors.hit}; color: #3a2a00;
    font-size: var(--text-base); box-shadow: 0 1px 6px ${({ theme }) => theme.colors.hit}66;
  }
`;
